import { useCallback, useMemo } from 'react';

import { useIsClient } from 'src/custom-hooks/use-client-snapshot';
import {
  matchDesktop,
  matchHeaderDesktop,
  matchLargeDesktop,
  matchLargeTablet,
  matchSmallTablet,
  useMatchMedia,
} from 'src/styling/media-queries';

export type UseWindowSizeBreakpoints = {
  isMobileOrSmaller: boolean;
  isSmallTabletOrSmaller: boolean;
  isTabletOrSmaller: boolean;
  isLargeDesktopOrSmaller: boolean;
  isMobileHeader: boolean;
  isWindowSizeReady: boolean;
};

const useWindowSize = (
  ownerDocumentRef?: React.RefObject<Element | null>
): UseWindowSizeBreakpoints => {
  const getOwnerWindow = useCallback(
    () => ownerDocumentRef?.current?.ownerDocument.defaultView ?? window,
    [ownerDocumentRef]
  );

  const isMobileOrSmaller = !useMatchMedia(matchSmallTablet, true, getOwnerWindow);
  const isSmallTabletOrSmaller = !useMatchMedia(matchLargeTablet, true, getOwnerWindow);
  const isTabletOrSmaller = !useMatchMedia(matchDesktop, true, getOwnerWindow);
  const isLargeDesktopOrSmaller = !useMatchMedia(matchLargeDesktop, true, getOwnerWindow);
  const isMobileHeader = !useMatchMedia(matchHeaderDesktop, true, getOwnerWindow);
  const isWindowSizeReady = useIsClient();

  return useMemo(
    () => ({
      isMobileOrSmaller,
      isSmallTabletOrSmaller,
      isTabletOrSmaller,
      isLargeDesktopOrSmaller,
      isMobileHeader,
      isWindowSizeReady,
    }),
    [
      isMobileOrSmaller,
      isSmallTabletOrSmaller,
      isTabletOrSmaller,
      isLargeDesktopOrSmaller,
      isMobileHeader,
      isWindowSizeReady,
    ]
  );
};

export default useWindowSize;
